import { Button, Flex, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import type { SVGAttributes } from 'react';

import FlagDE from '$src/components/SVGFlags/FlagDE';
import FlagES from '$src/components/SVGFlags/FlagES';
import FlagFR from '$src/components/SVGFlags/FlagFR';
import FlagIT from '$src/components/SVGFlags/FlagIT';
import FlagUK from '$src/components/SVGFlags/FlagUK';
import { useCustomRouter } from '$src/hooks/client';
import { AnalyticsEventName } from '$src/hooks/client/useTrackAnalytics';
import { useTrackTransition } from '$src/hooks/client/useTrackTransition';
import { ZInboundFormMessage } from '$src/models/state/message';

const languageOptions: Array<{
  label: z.infer<typeof ZInboundFormMessage.shape.language>;
  flagComponent: React.FC<SVGAttributes<SVGElement>>;
}> = [
  { label: 'de', flagComponent: FlagDE },
  { label: 'en', flagComponent: FlagUK },
  { label: 'es', flagComponent: FlagES },
  { label: 'fr', flagComponent: FlagFR },
  { label: 'it', flagComponent: FlagIT },
];

export const LanguageSelection = ({ theme }: { theme: 'light' | 'dark' }) => {
  const router = useCustomRouter();

  const [selectedLanguage, setSelectedLanguage] = useState(router.locale);

  const changeLanguage = useCallback(
    (locale: z.infer<typeof ZInboundFormMessage.shape.language>) => {
      setSelectedLanguage(locale);
      router.replace(router.pathname, router.asPath, { locale });
    },
    [router],
  );

  useTrackTransition({
    value: selectedLanguage,
    trueEventName: AnalyticsEventName.LANGUAGE_SELECTED,
    payload: { language: selectedLanguage },
    skipInitial: true,
  });

  const selectedLanguageOption = useMemo(() => {
    return languageOptions.find((option) => option.label === selectedLanguage);
  }, [selectedLanguage]);

  return (
    <Menu matchWidth offset={[0, 6]}>
      <MenuButton
        as={Button}
        padding={0}
        minW="80px"
        variant="ghost"
        fontSize="12px"
        overflow="hidden"
        fontWeight="400"
        borderRadius="12px"
        color="var(--text, #1C1C1C)"
        bg="var(--bg-secondary, #F7F7F7)"
        _hover={{ backgroundColor: 'var(--border, #E0E0E0)' }}
        _active={{ backgroundColor: 'var(--border, #E0E0E0)' }}
        backdropFilter={theme === 'light' ? 'blur(2.5px)' : undefined}
        data-ph-capture-attribute-tracking-id="language-selection-button"
      >
        <Flex paddingX="16px" paddingY="10px" gap="6px" alignItems="center">
          {selectedLanguageOption?.flagComponent && (
            <selectedLanguageOption.flagComponent
              width="22px"
              style={{ borderRadius: '2px', flexShrink: 0 }}
            />
          )}
          <span style={{ flexGrow: 1 }}>{selectedLanguageOption?.label.toUpperCase()}</span>
        </Flex>
      </MenuButton>
      <MenuList
        minW="0"
        borderRadius="12px"
        padding="8px"
        zIndex={1500}
        bg="var(--card-bg, #FFF)"
        borderColor="var(--border, #E0E0E0)"
        color="var(--text, #1C1C1C)"
      >
        {languageOptions
          .filter(({ label }) => label !== selectedLanguage)
          .map(({ label, flagComponent: Flag }) => {
            return (
              <MenuItem
                key={label}
                gap="6px"
                fontSize="12px"
                alignItems="center"
                paddingX="8px"
                paddingY="10px"
                borderRadius="8px"
                onClick={() => changeLanguage(label)}
                bg="transparent"
                color="var(--text, #1C1C1C)"
                _hover={{ background: 'var(--border, #E0E0E0)' }}
                _focus={{ background: 'var(--border, #E0E0E0)' }}
                _active={{ background: 'var(--border, #E0E0E0)' }}
                data-ph-capture-attribute-tracking-id={`language-selection-item`}
              >
                <Flag style={{ borderRadius: '2px', flexShrink: 0 }} width="22px" />

                {label.toUpperCase()}
              </MenuItem>
            );
          })}
      </MenuList>
    </Menu>
  );
};
